import moment from 'moment'
import { formatForMomentYearFirst } from 'helpers/moment-helper'

/** @module MortMath */

/**
 * Calculates monthly payment for a given principal, interest rate, and loan term
 *
 * @param {object} loanInfo - Information about a loan
 * @param {float} loanInfo.principal
 * @param {float} loanInfo.monthlyInterestPercent - result of: [MortMath.monthlyInterestPercent]{@link module:MortMath.monthlyInterestPercent}
 * @param {integer} loanInfo.loanTermInMonths - loan term in years / 12
 *
 * @return {float} The monthly payment for a loan
 *
 * @example
 * principal              = 400000
 * monthlyInterestPercent = 0.003958333333333334
 * loanTermInMonths       = 360
 *
 * return = 2086.5893460124694
 *
 */
export function monthlyPayment({ principal, monthlyInterestPercent, loanTermInMonths }) {
  const i = monthlyInterestPercent
  const n = loanTermInMonths
  const p = principal
  const x = Math.pow((1 + i), n)

  const monthlyPayment = (p * i * x) / (x - 1)

  return monthlyPayment === Infinity ? NaN : monthlyPayment
}


/**
 * Calculates pmi from homePrice, downPayment, and downPaymentPercent
 *
 * @param {integer} homePrice
 * @param {integer} downPayment
 * @param {integer} downPaymentPercent
 *
 * @return {float} The pmi for a loan
 *
 * @example
 *
 * return = 77.58333333333333
 *
 */
export function calculatePMI(homePrice, downPayment, downPaymentPercent) {
  const pmiPercent = determinePMIPercentBracket(downPaymentPercent) / 100
  const principal  = homePrice - downPayment

  return pmiPercent * principal / 12
}


/**
 * Helper function for calculatePMI, returns the pmi percent bracket based on downPaymentPercent
 *
 * @param {integer} downPaymentPercent
 *
 * @return {Number} The pmi percent bracket for a given downPaymentPercent
 *
 * @example
 *
 * return = 0.38
 *
 */
function determinePMIPercentBracket(downPaymentPercent) {
  switch (true) {
    case downPaymentPercent < 5:
      return 1
    case downPaymentPercent < 10:
      return 0.75
    case downPaymentPercent < 15:
      return 0.5
    case downPaymentPercent < 20:
      return 0.38
    default:
      return 0
  }
}


/**
 * Calculates monthly interest percent from yearly interest percent
 *
 * @param {float} yearlyInterestPercent
 *
 * @return {float} The monthly interest percent
 *
 * @example
 * yearlyInterestPercent = 4.75
 *
 * return = 0.003958333333333334
 *
 */
export function monthlyInterestPercent(yearlyInterestPercent) {
  return (yearlyInterestPercent / 100) / 12
}


/**
 * Calculates the amortization schedule for a loan
 *
 * @param {object} loanInfo - Information about a loan
 * @param {moment} loanInfo.date - Loan starting date
 * @param {float} loanInfo.monthlyInterestPercent - result of [MortMath.monthlyInterestPercent]{@link module:MortMath.monthlyInterestPercent}
 * @param {float} loanInfo.monthlyPayment - The monthly payment across the life of the loan
 * - result of [MortMath.monthlyPayment]{@link module:MortMath.monthlyPayment}
 * @param {float} loanInfo.balance
 * @param {float} loanInfo.monthInterestPayment - The portion of a given month's payment dedicated to paying interest
 * @param {float} loanInfo.monthPrincipalPayment - The portion of a given month's payment dedicated to paying down the balance
 * @param {object} loanInfo.schedule - The object holding loan month data. The accumulator in the interative recursion.
 * @param {moment} loanInfo.additionalPaymentStart - The date for the start of an additional payment
 * @param {integer} loanInfo.additionalPaymentAmount - The additional payment amount if any.
 *
 * @return {object} schedule
 *
 * @example
 *
 * loanInfo = {
 *   date: moment,
 *   monthlyInterestPercent: 0.0033333333333333335,
 *   monthlyPayment: 1432.2458863963586,
 *   balance: 300000,
 *   additionalPaymentStart: moment,
 *   additionalPaymentAmount: 0
 * }
 *
 *
 * return = {
 *   2010/6: {interest: 1000.0000000000001, principal: 432.2458863963585, balance: 299567.7541136036, displayDate: "6/10"},
 *   2010/7: {interest: 998.5591803786788, principal: 433.68670601767985, balance: 299134.0674075859, displayDate: "7/10"},
 *   2010/8: {interest: 997.1135580252865, principal: 435.13232837107216, balance: 298698.93507921486, displayDate: "8/10"},
 *   2010/9: {interest: 995.6631169307162, principal: 436.5827694656424, balance: 298262.3523097492, displayDate: "9/10"},
 *   ...,
 *   ...,
 *   many entries,
 *   ...,
 *   ...,
 *   2040/3: {interest: 14.2275036263711, principal: 1418.0183827699875, balance: 2850.232705141342, displayDate: "3/40"},
 *   2040/4: {interest: 9.500775683804475, principal: 1422.7451107125542, balance: 1427.487594428788, displayDate: "4/40"},
 *   2040/5: {interest: 4.758291981429294, principal: 1427.487594428788, balance: 0, displayDate: "5/40"},
 * }
 *
 */
export function amortizationSchedule({
  date,
  monthlyInterestPercent,
  monthlyPayment,
  balance,
  monthInterestPayment,
  monthPrincipalPayment,
  schedule = {},
  additionalPaymentStart,
  additionalPaymentAmount = 0
}) {
  date = date.clone()
  const formattedDate = date.format('YYYY/M')
  const displayDate   = date.format('M/YY')

  let newSchedule

  if (((monthInterestPayment + monthPrincipalPayment) > balance) || monthlyPayment == 0) {
    // base case to quit that recursive life
    monthInterestPayment = monthlyInterestPercent * balance

    schedule[formattedDate] = {
      interest:    monthInterestPayment,
      principal:   balance,
      balance:     0,
      displayDate: displayDate
    }

    newSchedule = schedule
  } else {
    // calculate the new values for data insertion and the next recursive round
    monthInterestPayment  = monthlyInterestPercent * balance
    monthPrincipalPayment = maybeAdditionalPayment(
        date,
        monthPrincipalPayment,
        additionalPaymentStart,
        additionalPaymentAmount,
        monthlyPayment,
        monthInterestPayment
    )
    balance               = balance - monthPrincipalPayment

    // data insertion
    schedule[formattedDate] = {
      interest:    monthInterestPayment,
      principal:   monthPrincipalPayment,
      balance:     balance,
      displayDate: displayDate
    }

    date.add(1, 'months')

    // recurse
    newSchedule =  amortizationSchedule({
      date:                    date,
      monthlyInterestPercent:  monthlyInterestPercent,
      monthlyPayment:          monthlyPayment,
      balance:                 balance,
      monthInterestPayment:    monthInterestPayment,
      monthPrincipalPayment:   monthPrincipalPayment,
      schedule:                schedule,
      additionalPaymentStart:  additionalPaymentStart,
      additionalPaymentAmount: additionalPaymentAmount
    })
  }

  return newSchedule
}


/**
 * Helper function for amortizationSchedule. Calculates the monthPrincipalPayment
 * with a chance that there will be an additional payment added in.
 *
 * @param {moment} date - Loan starting date
 * @param {float} monthPrincipalPayment - The portion of a given month's payment dedicated to paying down the balance
 * @param {moment} additionalPaymentStart - The date for the start of an additional payment
 * @param {integer} additionalPaymentAmount - The additional payment amount if any.
 * @param {float} monthlyPayment - The monthly payment across the life of the loan
 * @param {float} monthInterestPayment
 *
 * @return {float} monthPrincipalPayment
 *
 */
function maybeAdditionalPayment(date, monthPrincipalPayment, additionalPaymentStart, additionalPaymentAmount, monthlyPayment, monthInterestPayment) {
  monthPrincipalPayment = monthlyPayment - monthInterestPayment

  if (date >= additionalPaymentStart) {
    monthPrincipalPayment += additionalPaymentAmount
  }

  return monthPrincipalPayment
}


/**
 * Takes a monthly amortization schedule gives a yearly one
 *
 * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 *
 * @return {Object} yearly schedule
 *
 * @example
 *
 * input = {
 *   2018/8: {interest: 1025, principal: 281.768732741966, balance: 239718.23126725803, displayDate: "8/18"},
 *   2018/9: {interest: 1023.7966127039144, principal: 282.97212003805157, balance: 239435.25914721997, displayDate: "9/18"},
 *   2018/10: {interest: 1022.5880859412518, principal: 284.18064680071416, balance: 239151.07850041924, displayDate: "10/18"},
 *   2018/11: {interest: 1021.3743977622071, principal: 285.3943349797589, balance: 238865.68416543948, displayDate: "11/18"},
 *   2018/12: {interest: 1020.1555261232311, principal: 286.6132066187349, balance: 238579.07095882075, displayDate: "12/18"},
 *   2019/1: {interest: 1018.9314488866303, principal: 287.8372838553357, balance: 238291.23367496542, displayDate: "1/19"},
 *   ...,
 * }
 *
 * return = {
 *   2018: {interest: 5112.914622530605, principal: 1420.9290411792256, balance: 238579.07095882075, displayDate: "2018"},
 *   2019: {interest: 12144.877042317104, principal: 3536.3477505864857, balance: 235042.72320823427, displayDate: "2019"},
 *   2020: {interest: 11959.320826800806, principal: 3721.903966102788, balance: 231320.8192421315, displayDate: "2020"},
 *   2021: {interest: 11764.02826419844, principal: 3917.1965287051544, balance: 227403.62271342636, displayDate: "2021"},
 *   2022: {interest: 11558.488477230492, principal: 4122.736315673099, balance: 223280.88639775326, displayDate: "2022"},
 *   ...,
 * }
 */
export function yearlyAmortizationSchedule(schedule) {
  const scheduleKeys       = Object.keys(schedule)
  const scheduleKeysByYear = groupKeysByYear(scheduleKeys)
  const yearlyKeys         = Object.keys(scheduleKeysByYear)

  const yearlySchedule = yearlyKeys.reduce((accumulator, year) => {
    const interest  = scheduleKeysByYear[year].reduce((interest, scheduleKey) => {
      return interest += schedule[scheduleKey].interest
    }, 0)
    const principal = scheduleKeysByYear[year].reduce((principal, scheduleKey) => {
      return principal += schedule[scheduleKey].principal
    }, 0)

    const yearEnd   = scheduleKeysByYear[year].pop()
    const balance   = schedule[yearEnd].balance

    accumulator[year] = { interest: interest, principal: principal, balance: balance, displayDate: year }

    return accumulator
  }, {})

  return yearlySchedule
}


/**
 * Helper function that Groups schedule keys into year clusters for creating yearly data
 *
 * @param {Array.<string>} keys - The from a schedule object
 *
 * @return {Object} An object with each year in the loan as a key, with an array of date strings
 *
 * @example
 *
 * input = ["2010/6", "2010/7", "2010/8", "2010/9", "2010/10", "2010/11", "2010/12", "2011/1", ...]
 *
 * return = {
 *   2010: ["2010/6", "2010/7", "2010/8", "2010/9", "2010/10", "2010/11", "2010/12"],
 *   2011: ["2011/1", "2011/2", "2011/3", "2011/4", "2011/5", "2011/6", "2011/7", "2011/8", "2011/9", "2011/10", "2011/11", "2011/12"],
 *   2012: ["2012/1", "2012/2", "2012/3", "2012/4", "2012/5", "2012/6", "2012/7", "2012/8", "2012/9", "2012/10", "2012/11", "2012/12"],
 *   2013: ["2013/1", "2013/2", "2013/3", "2013/4", "2013/5", "2013/6", "2013/7", "2013/8", "2013/9", "2013/10", "2013/11", "2013/12"],
 *   2014: ["2014/1", "2014/2", "2014/3", "2014/4", "2014/5", "2014/6", "2014/7", "2014/8", "2014/9", "2014/10", "2014/11", "2014/12"],
 *   2015: ["2015/1", "2015/2", "2015/3", "2015/4", "2015/5", "2015/6", "2015/7", "2015/8", "2015/9", "2015/10", "2015/11", "2015/12"],
 *   2016: ["2016/1", "2016/2", "2016/3", "2016/4", "2016/5", "2016/6", "2016/7", "2016/8", "2016/9", "2016/10", "2016/11", "2016/12"],
 *   ...,
 * }
 */
function groupKeysByYear(keys) {
  return keys.reduce((accumulator, date) => {
    const year = date.split('/')[0]

    if (accumulator[year]) {
      accumulator[year].push(date)
    } else {
      accumulator[year] = [ date ]
    }

    return accumulator
  }, {})
}


/**
 * Sums the total interest amount of a loan
 *
 * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 *
 * @return {float} total interest
 *
 */
export function totalInterest(schedule) {
  return Object.keys(schedule).reduce((accumulator, month) => {
    accumulator += schedule[month].interest
    return accumulator
  }, 0)
}


/**
 * Takes a yearlySchedule and returns interest and principal accumulations over the life of the loan, as well as the running balance
 *
 * @param {Object} yearlySchedule - result of [MortMath.yearlyAmortizationSchedule]{@link module:MortMath.yearlyAmortizationSchedule}
 *
 * @return {Object} data
 * @return {Array.<float>} data.interestData - values increasing by each years interest payments
 * @return {Array.<float>} data.principalData - values incresing by each years principal payments
 * @return {Array.<float>} data.balanceData - values decreasing as the loan is paid down
 *
 * @example
 * return = {
 *   interestData:  [ 5098.822433980482, 17185.782102743888, 29053.507463941045, 40692.782150968036, ... ],
 *   principalData: [ 2110.342336927378, 7325.378118342836, 12759.648207324546, 18422.368970476415, ... ],
 *   balanceData:   [ 295389.6576630726, 290174.62188165705, 284740.3517926753, 279077.63102952344, ... ],
 * }
 */
export function yearlyOverallTrendingValues(yearlySchedule) {
  const yearlyKeys = Object.keys(yearlySchedule)

  const interestData  = []
  const principalData = []
  const balanceData   = []
  let interestTotal   = 0
  let principalTotal  = 0

  yearlyKeys.forEach(year => {
    interestTotal  += yearlySchedule[year].interest
    principalTotal += yearlySchedule[year].principal

    interestData.push(interestTotal)
    principalData.push(principalTotal)
    balanceData.push(yearlySchedule[year].balance)
  })

  return {
    interestData,
    principalData,
    balanceData
  }
}


/**
 * Sums the total interest amount of a loan from a given date forward.
 * Used to compare the original and new loan interest totals.
 *
 * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {moment} date to start summing the interest, if the original loan started in the past this might be the new loan start date.
 *
 * @return {Float} Interest summed from the given date
 *
 */
export function totalInterestFrom(schedule, date) {
  const keys          = Object.keys(schedule)
  const formattedDate = date.format('YYYY/M')
  const indexToCut    = keys.indexOf(formattedDate)
  const filteredKeys  = keys.slice(indexToCut)

  return filteredKeys.reduce((accumulator, month) => {
    accumulator += schedule[month].interest
    return accumulator
  }, 0)
}


/**
 * Accumulates year end balances across a loan from a given date
 *
 * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {moment} date
 * @param {String} attributeOfInterest - 'interest' or 'principal' etc.
 *
 * @return {Array.<float>}
 *
 */
export function attributeOfInterestByYearFrom(schedule, date, attributeOfInterest) {
  const keys = Object.keys(schedule)

  const attributeSet = keys.reduce((accumulator, month) => {
    const monthDate = moment(formatForMomentYearFirst(month))

    if (monthDate >= date && monthDate.month() === 11) {
      accumulator.push(schedule[month][attributeOfInterest])
    }

    return accumulator
  }, [])

  if (attributeSet[attributeSet.length - 1] !== 0) {
    attributeSet.push(0)
  }

  return attributeSet
}

/**
 * Calculates monthly payment based on monthly income and a target dti
 *
 * @param {object} data - Information about a loan
 * @param {float} data.dtiPercent
 * @param {float} data.monthlyIncome
 * @param {integer} data.monthlyDebt
 * @param {integer} data.monthlyHoa
 * @param {integer} data.monthlyInsurance
 *
 * @return {float} The monthly payment for a loan
 *
 * @example
 * {dtiPercent: 0.36, monthlyIncome: 5416.6, monthlyDebt: 900, monthlyHoa: 0, monthlyInsurance: 66.6}
 * return: 983
 */
export function monthlyPaymentByIncome({ dtiPercent, monthlyIncome, monthlyDebt, monthlyHoa, monthlyInsurance }) {
  return (dtiPercent * monthlyIncome) - monthlyDebt - monthlyHoa - monthlyInsurance
}

/**
 * Calculates monthly payment based on maximum monthly income, hoa, insurance
 *
 * @param {object} data - Information about a loan
 * @param {integer} data.maximumMonthlyPayment
 * @param {integer} data.monthlyHoa
 * @param {integer} data.monthlyInsurance
 *
 * @return {float} The monthly payment for a loan
 *
 * @example
 * {maximumMonthlyPayment: 1800, monthlyHoa: 0, monthlyInsurance: 66.6}
 * return: 983
 */
export function monthlyPaymentByPayment({ maximumMonthlyPayment, monthlyHoa, monthlyInsurance }) {
  return maximumMonthlyPayment - monthlyHoa - monthlyInsurance
}

/**
 * Calculates total affordable loan amount
 *
 * @param {object} data - Information about a loan
 * @param {integer} data.monthlyPayment
 * @param {float} data.interestRate
 * @param {integer} data.loanLength
 * @param {float} data.monthlyTaxRate
 * @param {integer} data.monthlyTaxes
 *
 * @return {float} total affordable loan amount
 *
 * @example
 * {monthlyPayment: 2000, interestRate: 0.02, loanLength: 360, monthlyTaxRate: 0.0005}
 * return: 476622
 */
export function loanAmount({ monthlyPayment, interestRate, loanLength, monthlyTaxRate, monthlyTaxes = 0 }) {
  if (monthlyTaxes !== 0) monthlyTaxRate = 0
  const monthlyInterestRate = interestRate / 12
  const monthlyInterest = Math.pow(1 + monthlyInterestRate, loanLength)

  return ((monthlyPayment - monthlyTaxes) * ( monthlyInterest - 1 )) / (monthlyInterestRate * monthlyInterest + (monthlyTaxRate * monthlyInterest) - monthlyTaxRate)
}

/**
 * Calculates pmi multiplier based on down payment percent
 *
 * @param {float} down payment percent
 * @note These constants are magic industry values, only change these if you know what you are doing
 *
 * @return {float} pmi multiplier
 */
export function pmiMultiplier(downPaymentPercent) {
  switch (true) {
    case downPaymentPercent < 0.05:
      return 0.866747
    case downPaymentPercent < 0.1:
      return 0.893476
    case downPaymentPercent < 0.15:
      return 0.915285
    case downPaymentPercent < 0.2:
      return 0.935432
    default:
      return 1
  }
}
